import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { tv } from "tailwind-variants";

import { cn } from "../../utils/cn";

import type { ImgHTMLAttributes, ReactNode } from "react";
import type { VariantProps } from "tailwind-variants";
import type { SlotsToClasses } from "../../utils/types";

const imageVariants = tv({
  slots: {
    base: "inline-block",
    img: "block max-w-full",
    wrapper: "relative inline-block cursor-pointer",
    mask: [
      "absolute",
      "inset-0",
      "flex",
      "items-center",
      "justify-center",
      "bg-black/50",
      "opacity-0",
      "transition-opacity",
      "duration-200",
      "group-hover:opacity-100",
    ],
    lightboxOverlay: [
      "fixed",
      "inset-0",
      "z-50",
      "flex",
      "items-center",
      "justify-center",
      "bg-black/80",
      "backdrop-blur-sm",
      "animate-[fade-in_200ms_ease-out]",
    ],
    lightboxImage: [
      "max-h-[90vh] max-w-[90vw] object-contain",
      "animate-[scale-in_200ms_ease-out]",
    ],
    lightboxCloseButton: [
      "absolute",
      "top-4",
      "right-4",
      "flex",
      "h-10",
      "w-10",
      "items-center",
      "justify-center",
      "rounded-full",
      "bg-white/10",
      "text-white",
      "transition-colors",
      "hover:bg-white/20",
      "cursor-pointer",
    ],
  },
  variants: {
    objectFit: {
      contain: { img: "object-contain" },
      cover: { img: "object-cover" },
      fill: { img: "object-fill" },
      none: { img: "object-none" },
      scaleDown: { img: "object-scale-down" },
    },
    previewEnabled: {
      true: { wrapper: "cursor-pointer" },
      false: { wrapper: "cursor-default" },
    },
  },
  defaultVariants: {
    objectFit: undefined,
    previewEnabled: true,
  },
});

/**
 * Configuration object for the preview/lightbox feature.
 */
export interface ImagePreviewConfig {
  /**
   * Custom content to show as hover overlay mask.
   */
  mask?: ReactNode;
  /**
   * Custom icons for the lightbox.
   */
  icons?: {
    /**
     * Custom close button icon.
     */
    close?: ReactNode;
  };
  /**
   * Whether the mask is visible on hover. Defaults to true when mask is provided.
   */
  maskVisible?: boolean;
}

export type ImageVariantProps = VariantProps<typeof imageVariants>;
export type ImageSlots = keyof ReturnType<typeof imageVariants>;

export interface ImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, "children">,
    ImageVariantProps {
  /**
   * Classes object to style the image and its children.
   */
  classNames?: SlotsToClasses<ImageSlots>;
  /**
   * Image source URL.
   */
  src?: string;
  /**
   * Alternative text description of the image.
   */
  alt?: string;
  /**
   * Image width in pixels or CSS value.
   */
  width?: number | string;
  /**
   * Image height in pixels or CSS value.
   */
  height?: number | string;
  /**
   * Fallback image URL to display if the main image fails to load.
   */
  fallback?: string;
  /**
   * Whether to enable preview/lightbox functionality, or a configuration object.
   * - `true` (default): Enable lightbox with default settings
   * - `false`: Disable lightbox completely
   * - `{ mask: ReactNode }`: Enable lightbox with custom hover overlay
   * - `{ icons: { close: ReactNode } }`: Enable lightbox with custom close icon
   */
  preview?: boolean | ImagePreviewConfig;
  /**
   * Callback when the image fails to load.
   */
  onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  /**
   * Callback when the image loads successfully.
   */
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

/**
 * An image component with optional lightbox/preview functionality.
 *
 * @example
 * // Basic usage with lightbox enabled by default
 * <Image src="/path/to/image.jpg" alt="Description" />
 *
 * @example
 * // Disable lightbox
 * <Image src="/path/to/image.jpg" preview={false} alt="Description" />
 *
 * @example
 * // With custom hover mask
 * <Image
 *   src="/path/to/image.jpg"
 *   preview={{ mask: <div className="text-white">Click to enlarge</div> }}
 *   alt="Description"
 * />
 *
 * @example
 * // With custom close icon
 * <Image
 *   src="/path/to/image.jpg"
 *   preview={{ icons: { close: <CustomCloseIcon /> } }}
 *   alt="Description"
 * />
 */
export const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      className,
      classNames,
      style,
      src,
      alt = "",
      width,
      height,
      fallback,
      objectFit,
      onError,
      onLoad,
      preview = true,
      ...props
    },
    ref
  ) => {
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    // Parse preview prop
    const previewConfig = useMemo((): {
      enabled: boolean;
      mask?: ReactNode;
      closeIcon?: ReactNode;
    } => {
      if (preview === false) {
        return { enabled: false };
      }
      if (preview === true) {
        return { enabled: true };
      }
      // preview is an object
      return {
        enabled: true,
        mask: preview.mask,
        closeIcon: preview.icons?.close,
      };
    }, [preview]);

    const slots = useMemo(
      () => imageVariants({ objectFit, previewEnabled: previewConfig.enabled }),
      [objectFit, previewConfig.enabled]
    );
    const { img, wrapper, mask, lightboxOverlay, lightboxImage, lightboxCloseButton } = slots;

    const imageStyle = useMemo(() => {
      const dimensionStyle: React.CSSProperties = {};
      if (width !== undefined) {
        dimensionStyle.width = typeof width === "number" ? `${width}px` : width;
      }
      if (height !== undefined) {
        dimensionStyle.height = typeof height === "number" ? `${height}px` : height;
      }
      return { ...dimensionStyle, ...style };
    }, [width, height, style]);

    const handleError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (fallback && event.currentTarget.src !== fallback) {
        event.currentTarget.src = fallback;
      }
      onError?.(event);
    };

    const handleImageClick = useCallback(() => {
      if (previewConfig.enabled) {
        setIsLightboxOpen(true);
      }
    }, [previewConfig.enabled]);

    const handleCloseLightbox = useCallback(() => {
      setIsLightboxOpen(false);
    }, []);

    const handleBackdropClick = useCallback(
      (event: React.MouseEvent) => {
        // Only close if clicking on the backdrop, not the image
        if (event.target === event.currentTarget) {
          handleCloseLightbox();
        }
      },
      [handleCloseLightbox]
    );

    // Handle Escape key
    useEffect(() => {
      if (!isLightboxOpen) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          handleCloseLightbox();
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [isLightboxOpen, handleCloseLightbox]);

    // If preview is disabled, render simple image
    if (!previewConfig.enabled) {
      return (
        <img
          ref={ref}
          className={cn(img(), className, classNames?.img)}
          src={src}
          alt={alt}
          style={imageStyle}
          onError={handleError}
          onLoad={onLoad}
          {...props}
        />
      );
    }

    // Render image with preview/lightbox support
    return (
      <>
        {}
        <div
          className={cn(wrapper(), "group overflow-hidden", className, classNames?.wrapper)}
          style={imageStyle}
          onClick={handleImageClick}
        >
          <img
            ref={ref}
            className={cn(img(), "size-full", classNames?.img)}
            src={src}
            alt={alt}
            onError={handleError}
            onLoad={onLoad}
            {...props}
          />
          {previewConfig.mask && (
            <div className={cn(mask(), classNames?.mask)}>{previewConfig.mask}</div>
          )}
        </div>

        {/* Lightbox overlay */}
        {isLightboxOpen &&
          createPortal(
            <div
              className={cn(lightboxOverlay(), classNames?.lightboxOverlay)}
              onClick={handleBackdropClick}
              role="dialog"
              aria-modal="true"
              aria-label={alt ? `Enlarged view of: ${alt}` : "Enlarged image view"}
            >
              <img className={cn(lightboxImage(), classNames?.lightboxImage)} src={src} alt={alt} />
              {previewConfig.closeIcon ? (
                <button
                  className="absolute right-4 top-4 cursor-pointer border-none bg-transparent text-white"
                  onClick={handleCloseLightbox}
                  aria-label="Close lightbox"
                  type="button"
                >
                  {previewConfig.closeIcon}
                </button>
              ) : (
                <button
                  className={cn(lightboxCloseButton(), classNames?.lightboxCloseButton)}
                  onClick={handleCloseLightbox}
                  aria-label="Close lightbox"
                  type="button"
                >
                  <FontAwesomeIcon icon={faXmark} size="lg" />
                </button>
              )}
            </div>,
            document.body
          )}
      </>
    );
  }
);
Image.displayName = "Image";
